import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import './LogoDivider.css';

const SCROLL_DURATION = 10; // seconds

const LogoDivider: React.FC = () => {
  // State for block width and repeat count
  const [blockWidth, setBlockWidth] = useState(0);
  const [repeatCount, setRepeatCount] = useState(8);
  const blockRef = useRef<HTMLDivElement>(null);

  // Animation controls
  const controls = useAnimation();
  const controls2 = useAnimation();

  // Measure block width and calculate repeat count
  useEffect(() => {
    const updateRepeat = () => {
      if (blockRef.current) {
        const width = blockRef.current.offsetWidth;
        setBlockWidth(width);
        const count = Math.ceil(window.innerWidth / width) + 2;
        setRepeatCount(count);
        // Log for debugging
        console.log('[LogoDivider] blockWidth:', width, 'repeatCount:', count, 'windowWidth:', window.innerWidth);
      }
    };
    updateRepeat();
    window.addEventListener('resize', updateRepeat);
    return () => window.removeEventListener('resize', updateRepeat);
  }, []);

  // Animation loop for top marquee (right to left)
  useEffect(() => {
    if (blockWidth === 0) return;
    const totalWidth = blockWidth * repeatCount;
    // Log for debugging
    console.log('[LogoDivider] TopMarquee totalWidth:', totalWidth);
    const animate = async () => {
      await controls.start({
        x: -totalWidth,
        transition: {
          duration: SCROLL_DURATION,
          ease: 'linear'
        }
      });
      controls.set({ x: 0 });
      animate();
    };
    controls.set({ x: 0 });
    animate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockWidth, repeatCount]);

  // Animation loop for bottom marquee (left to right)
  useEffect(() => {
    if (blockWidth === 0) return;
    const totalWidth = blockWidth * repeatCount;
    // Log for debugging
    console.log('[LogoDivider] BottomMarquee totalWidth:', totalWidth);
    const animate = async () => {
      await controls2.start({
        x: 0,
        transition: {
          duration: SCROLL_DURATION,
          ease: 'linear'
        }
      });
      controls2.set({ x: -totalWidth });
      animate();
    };
    controls2.set({ x: -totalWidth });
    animate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockWidth, repeatCount]);

  // Top Marquee Content (blue)
  const MarqueeContent = React.forwardRef<HTMLDivElement>((props, ref) => (
    <div ref={ref} className='flex items-center gap-[34px]'>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-white ml-[34px]'>Grow</span>
      <div className='w-[15px] h-[15px] rounded-full bg-white'></div>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-white'>Design</span>
      <div className='w-[15px] h-[15px] rounded-full bg-white'></div>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-white'>Build</span>
      <div className='w-[15px] h-[15px] rounded-full bg-white'></div>
    </div>
  ));

  // Bottom Marquee Content (white bg, dark text)
  const MarqueeContent2 = React.forwardRef<HTMLDivElement>((props, ref) => (
    <div ref={ref} className='flex items-center gap-[34px]'>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-[#100E12] ml-[34px]'>Grow</span>
      <div className='w-[15px] h-[15px] rounded-full bg-[#100E12]'></div>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-[#100E12]'>Design</span>
      <div className='w-[15px] h-[15px] rounded-full bg-[#100E12]'></div>
      <span className='font-normal text-[48px] max-[560px]:text-3xl text-[#100E12]'>Build</span>
      <div className='w-[15px] h-[15px] rounded-full bg-[#100E12]'></div>
    </div>
  ));

  return (
    <div
      className='w-full overflow-hidden h-[450px] max-[560px]:h-[300px] flex flex-col items-center justify-center'
      style={{ background: 'transparent' }}
    >
      {/* Top Marquee */}
      <div
        className='logo-divider-container w-full relative h-[120px] max-[560px]:h-[100px]'
        style={{ zIndex: 2, position: 'relative', background: '#2563EB', transform: 'rotate(2deg)' }}
      >
        <div className='logo-divider-scroll w-full h-[60px] relative' style={{ maxWidth: '100%' }}>
          <motion.div
            className='logo-divider-marquee flex flex-row absolute left-0 top-0'
            animate={controls}
            style={{ width: 'fit-content', height: 60 }}
          >
            {/* Render enough blocks to cover 2x screen width */}
            {Array.from({ length: repeatCount * 2 }).map((_, i) =>
              i === 0 ? <MarqueeContent key={i} ref={blockRef} /> : <MarqueeContent key={i} />
            )}
          </motion.div>
        </div>
      </div>
      {/* Gap */}
      {/* Bottom Marquee */}
      <div
        className='logo-divider-container w-full relative mt-[-40px] border-y border-[#DDDDDD]  h-[120px] max-[560px]:h-[100px]'
        style={{ zIndex: 1, position: 'relative', background: '#fff', transform: 'rotate(-5deg)' }}
      >
        <div className='logo-divider-scroll w-full h-[60px] relative' style={{ maxWidth: '100%' }}>
          <motion.div
            className='logo-divider-marquee flex flex-row absolute left-0 top-0'
            animate={controls2}
            style={{ width: 'fit-content', height: 60 }}
          >
            {Array.from({ length: repeatCount * 2 }).map((_, i) =>
              i === 0 ? <MarqueeContent2 key={i} ref={blockRef} /> : <MarqueeContent2 key={i} />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LogoDivider;
